import React from 'react';
import styled from 'libs/styled';
import Text from 'components/Text';
import Button from 'components/Button';
import Link from '@fogia/core-web/components/Link';
import ChevronLeft from 'assets/media/icons/ChevronLeft';
import ChevronRight from 'assets/media/icons/ChevronRight';
import type { SliderHeaderProps } from './ContentSlider/types';

const HEADER_BOTTOM_MARGIN = '10px';

/** Figma: header row hug ~18px; chevron hit area is taller and bleeds outside this band when centered. */
const SLIDER_HEADER_ROW_HEIGHT_PX = 18;
const NAV_CHEVRON_PADDING_Y_PX = 8;
const NAV_CHEVRON_PADDING_X_PX = 12;

const Header = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-column: 1 / -1;
    margin-bottom: ${HEADER_BOTTOM_MARGIN};
    padding: 0 var(--grid-padding, 10px);
    overflow: visible;
`;

const TitleWrapper = styled('div')`
    flex: 1;
`;

const HeaderRight = styled('div')`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    overflow: visible;
`;

/**
 * Fixed row band height so padded chevrons do not grow the header (36px → 18px band).
 * Buttons use Figma padding 8×12 around the icon; overflow visible lets the hit area bleed vertically.
 */
const NavButtonsWrapper = styled('div')`
    display: flex;
    align-items: center;
    height: ${SLIDER_HEADER_ROW_HEIGHT_PX}px;
    min-height: 0;
    overflow: visible;
    flex-shrink: 0;

    & > * {
        padding: ${NAV_CHEVRON_PADDING_Y_PX}px ${NAV_CHEVRON_PADDING_X_PX}px;
        box-sizing: border-box;
    }
`;

const DEFAULT_TYPOGRAPHY: Array<string | null> = ['Primary/Mobile/18_120_510_-3', null, null, 'Primary/15_120_510_-1'];

/** Title, optional link, optional prev/next chevrons; wire nav via navApi from Desktop/Mobile onReady. */
const SliderHeader: React.FC<SliderHeaderProps> = ({
    leftSlot,
    title = '',
    titleLevel = 'h2',
    titleTypography = DEFAULT_TYPOGRAPHY,
    link,
    sanityLink,
    linkTypography = DEFAULT_TYPOGRAPHY,
    onLinkClick,
    linkLabel,
    navApi,
    showChevrons = false,
}) => {
    const linkConfig =
        sanityLink != null
            ? { sanityLink: sanityLink.sanityLink, label: sanityLink.label ?? '' }
            : link != null
              ? { url: link.href, label: link.label }
              : null;

    const hasTitle = Boolean(title?.trim());
    const hasLeft = leftSlot != null || hasTitle;
    const hasLink = linkConfig != null || (onLinkClick != null && Boolean(linkLabel));
    if (!hasLeft && !hasLink && !showChevrons) {
        return null;
    }

    const titleHeading = hasTitle ? (
        <Text $as={titleLevel} typography={titleTypography} m="0">
            {title}
        </Text>
    ) : null;

    const leftColumnContent = leftSlot != null ? leftSlot : titleHeading;
    const hasLeftColumn = leftColumnContent != null;

    return (
        <Header>
            {hasLeftColumn ? <TitleWrapper>{leftColumnContent}</TitleWrapper> : null}
            <HeaderRight marginLeft={hasLeftColumn ? null : 'auto'}>
                {onLinkClick && linkLabel ? (
                    <Button type="button" theme="textButtonUnderline" typography={linkTypography} onClick={onLinkClick}>
                        {linkLabel}
                    </Button>
                ) : linkConfig ? (
                    <Button
                        as={Link}
                        {...('sanityLink' in linkConfig
                            ? { sanityLink: linkConfig.sanityLink }
                            : { url: linkConfig.url })}
                        theme="textButtonUnderline"
                        typography={linkTypography}
                    >
                        {linkConfig.label}
                    </Button>
                ) : null}
                {navApi && showChevrons ? (
                    <NavButtonsWrapper>
                        <Button
                            type="button"
                            theme="textButton"
                            iconOnly
                            disabled={!navApi.canGoPrev}
                            onClick={() => navApi.scrollPrev()}
                            aria-label="Previous"
                        >
                            <ChevronLeft size="small" />
                        </Button>
                        <Button
                            type="button"
                            theme="textButton"
                            iconOnly
                            disabled={!navApi.canGoNext}
                            onClick={() => navApi.scrollNext()}
                            aria-label="Next"
                        >
                            <ChevronRight size="small" />
                        </Button>
                    </NavButtonsWrapper>
                ) : null}
            </HeaderRight>
        </Header>
    );
};

export default SliderHeader;
